import { DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as turf from '@turf/turf';
import * as mapboxgl from 'mapbox-gl';
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import { Site } from '@shared/models';
import { MapControlState } from '@store/map-control/map-control.reducer';
import { environment } from '@env/environment';
import { GisService } from '@core/api/gis.service';
import { selectIsMobileView } from '@store/app-config/app-config.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import * as fromApp from '@store';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  destroyRef = inject(DestroyRef);
  currentMap = null;
  // Toggle Roads
  layers = [
    'road-street-case',
    'road-street-low',
    'road-minor-case',
    'road-minor-low',
    'road-secondary-tertiary-case',
    'road-motorway-trunk-case',
    'road-major-link-case',
    'road-primary-case',
    'road-major-link',
    'road-minor',
    'road-street',
    'road-secondary-tertiary',
    'road-primary',
    'road-motorway-trunk',
    'road-label',
    'road-label-outdoors',
    'road-number-shield',
    'road-exit-shield',
  ];
  isMobile = false;

  constructor(
    readonly gisService: GisService,
    readonly store: Store<fromApp.AppState>,
  ) {

    this.store
      .select(selectIsMobileView)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mobileViewMode => {
        this.isMobile = mobileViewMode;
      });
  }

  setRoadLayerVisibility(map: mapboxgl.Map, mapControls: MapControlState) {
    this.currentMap?.getStyle().layers.forEach(layer => {
      if (layer["source-layer"] == 'road') {
        map.setLayoutProperty(layer.id, 'visibility', mapControls.displayRoads ? 'visible' : 'none');
      }
    })
  }

  flyToBoundingBox(feature: any) {
    if (this.currentMap) {
      const boundingBox = turf.bbox({
        type: 'FeatureCollection',
        features: [feature],
      }) as any;
      this.currentMap.fitBounds(boundingBox, {
        padding: this.isMobile ? 5 : 200,
        animate: true,
      });
    }
  }

  navigateToMap(router: Router, site: Site, prefix: string) {
    if (site.versions[0].parcels.length > 0) {
      router.navigate([prefix, site.id, 'map-site']);
    } else {
      router.navigate([prefix, site.id, 'locate-property', 'select-parcels']);
    }
  }

  toggleLayer(layerId: string, propertyName: string, propertyValue: string) {
    if (this.currentMap?.getLayer(layerId) !== undefined) {
      this.currentMap.setLayoutProperty(layerId, propertyName, propertyValue);
    }
  }

  setLayerVisibility(currentMapControls: MapControlState) {
    this.toggleLayer('kml-import', 'visibility', currentMapControls.displayKMLUpload ? 'visible' : 'none');
    this.toggleLayer('kml-import-outline', 'visibility', currentMapControls.displayKMLUpload ? 'visible' : 'none');
    this.toggleLayer('waterways-data1', 'visibility', currentMapControls.displayWaterways ? 'visible' : 'none');
    this.toggleLayer('waterways-data2', 'visibility', currentMapControls.displayWaterways ? 'visible' : 'none');
    this.toggleLayer('forest-data', 'visibility', currentMapControls.displayForest ? 'visible' : 'none');
    this.toggleLayer('public-land-data', 'visibility', currentMapControls.displayPublicLand ? 'visible' : 'none');

    this.setRoadLayerVisibility(this.currentMap, currentMapControls);
  }

  addForestSource(forestData: any, layerName: string) {
    if (this.currentMap.getSource(layerName)) {
      const style = this.currentMap.getStyle();
      // Find all layers using this source
      const layersUsingSource = style.layers.filter(l => l.source === layerName);
      // Remove them in reverse order (safer if there are dependencies)
      for (let i = layersUsingSource.length - 1; i >= 0; i--) {
        const layerId = layersUsingSource[i].id;
        if (this.currentMap.getLayer(layerId)) {
          this.currentMap.removeLayer(layerId);
        }
      }
      // Finally remove the source
      this.currentMap.removeSource(layerName)
    }
    this.currentMap.addSource(layerName, {
      type: 'geojson',
      data: forestData,
    });
  }

  setForestData(source: string, layer: string) {
    let baseConfig: any = {
      source: source
    }
    if (layer) {
      baseConfig['source-layer'] = layer
    }

    this.removeLayer('forest-data');
    this.removeLayer('forest-data-outline');
    this.removeLayer('forest-data-highlighted');

    this.currentMap.addLayer({
      ...baseConfig,
      id: 'forest-data',
      type: 'fill',
      paint: {
        'fill-outline-color': 'rgba(72,210,7,0.0)',
        'fill-color': '#48D207',
        'fill-opacity': 0.5,
      }
    });

    this.currentMap.addLayer({
      ...baseConfig,
      id: 'forest-data-outline',
      type: 'line',
      paint: {
        'line-width': 1.15,
        'line-color': 'rgba(72,210,7,0.0)',
      }
    });

    this.currentMap.addLayer({
      ...baseConfig,
      id: 'forest-data-highlighted',
      type: 'fill',
      paint: {
        'fill-outline-color': '#4A90E2',
        'fill-color': 'rgb(63, 121, 174)', // '#6e599f',
        'fill-opacity': 0.75,
      },
      filter: ['in', '$id', '']
    });

  }

  removeLayer(layer: string) {
    if (this.currentMap.getLayer(layer)) {
      this.currentMap.removeLayer(layer)
    }
  }

  addRemovalLayer() {
    this.currentMap.addLayer({
      source: 'new-stand-data-source',
      id: 'new-stand-data',
      type: 'fill',
      paint: {
        'fill-pattern': 'removal-fill',
        'fill-color': '#289289',
        'fill-opacity': 0.5,
      }
    });

  }

  createReverseGeolocatorWithParcel(classname: string, functionToCall: any) {
    let reverseGeocoder = new MapboxGeocoder({
      accessToken: environment.mapbox.accessToken,
      localGeocoder: this.coordinatesGeocoder,
      placeholder: 'Address, Lat/Long, Parcel Number',
      countries: 'us',
      reverseGeocode: true,
      limit: 10,  // allow up to 10 displayed items
    });
    let lastParcelResults: any[] = [];
    reverseGeocoder.addTo(classname);
    reverseGeocoder.on('results', async (e) => {
      const query = e.config?.query?.trim();
      if (query && typeof query == 'string' && query.length >= 3) {
        try {
          const parcels = await this.gisService.getParcelListLikeParcelNumberAsync(query, 5);
          lastParcelResults = parcels.map(parcel => ({
            center: parcel.coordinates,
            geometry: { type: 'Point', coordinates: parcel.coordinates },
            place_name: `Parcel Match: ${parcel.parcelNumber}, ${parcel.county} County, ${parcel.state}`,
            place_type: ['parcel'],
            properties: { isCustom: true },
            type: 'Feature',
          }));
        } catch {
          lastParcelResults = [];
        }
      } else {
        lastParcelResults = [];
      }

      // Always merge current Mapbox results with latest parcel results
      const merged = [...lastParcelResults, ...e.features.slice(0, 5)];
      reverseGeocoder._typeahead.update(merged);
    });
    reverseGeocoder.on('result', item => functionToCall(item));
    return reverseGeocoder;
  }

  coordinatesGeocoder(query: string): RegExpMatchArray | any[] {
    // match anything which looks like a decimal degrees coordinate pair
    const matches = /^\s*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)\s*$/.exec(query);
    if (!matches) {
      return null;
    }

    const coord1 = Number(matches[1]);
    const coord2 = Number(matches[2]);

    return [{
      center: [coord2, coord1],
      geometry: {
        type: 'Point',
        coordinates: [coord2, coord1],
      },
      place_name: `Lat: ${coord1} Long: ${coord2}`,
      place_type: ['coordinate'],
      properties: { isCustom: true },
      type: 'Feature',
    }];
  }
}
